import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSwaggerAvailable, swaggerUiUrl, apiOrigin } from '../hooks/useSwaggerAvailable';

/**
 * OpenApiExportLinks — queue #668 §UAT-2026-07-09-R668 §R668-2.
 *
 * 모듈 상세 publicApis 섹션에 ① OpenAPI JSON 다운로드 ② swagger-ui group 딥링크를 묶어
 * 노출한다. 가용성 게이트·origin 헬퍼는 §R645 `useSwaggerAvailable`/`swaggerUiUrl`/`apiOrigin`
 * 을 그대로 재사용(신규 프로브/hook 0). springdoc 은 3 path-group(biz/admin/gateway)만
 * 실존하므로(§R668-1) per-capability(C0xx) 필터는 제공하지 않는다 — 캡션으로 명시.
 *
 * 5상태: `useSwaggerAvailable` 가 pending(loading)·false(empty/error 흡수) 이면 전역
 * 미렌더(§R645 dead-link 0 계보) · `true` 일 때만 default 렌더(다운로드+딥링크+select+캡션).
 */

type DocGroup = '' | 'biz' | 'admin' | 'gateway';

export function OpenApiExportLinks() {
  const { t } = useTranslation();
  const { data: swaggerAvailable } = useSwaggerAvailable();
  const [group, setGroup] = useState<DocGroup>('');

  if (!swaggerAvailable) return null;

  const downloadHref = group ? `${apiOrigin()}/v3/api-docs/${group}` : `${apiOrigin()}/v3/api-docs`;
  const swaggerHref = group ? `${swaggerUiUrl()}?urls.primaryName=${group}` : swaggerUiUrl();

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', flexWrap: 'wrap' }}>
      <label
        htmlFor="showcase-openapi-group"
        style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}
      >
        {t('showcase.openapi.group.label')}
      </label>
      <select
        id="showcase-openapi-group"
        value={group}
        onChange={(e) => setGroup(e.target.value as DocGroup)}
        style={{
          fontSize: 'var(--font-size-xs)',
          padding: '2px var(--spacing-1)',
          borderRadius: 'var(--radius-sm)',
          border: '1px solid var(--color-surface-border)',
        }}
      >
        <option value="">{t('showcase.openapi.group.all')}</option>
        <option value="biz">{t('showcase.openapi.group.biz')}</option>
        <option value="admin">{t('showcase.openapi.group.admin')}</option>
        <option value="gateway">{t('showcase.openapi.group.gateway')}</option>
      </select>
      <a
        href={downloadHref}
        download
        target="_blank"
        rel="noopener noreferrer"
        style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-info)' }}
      >
        {t('showcase.openapi.download')}
      </a>
      <a
        href={swaggerHref}
        target="_blank"
        rel="noopener noreferrer"
        title={t('showcase.tryit.swaggerHint')}
        style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-info)' }}
      >
        {t('showcase.tryit.swaggerLink')}
      </a>
      <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', width: '100%' }}>
        {t('showcase.openapi.caption')}
      </span>
    </div>
  );
}
